<template>
  <div class="background pb-5">
    <!-- Desktop Version -->
    <div class="d-flex justify-center align-center background signup-desktop" v-if="!mobile">
      <!-- left side: decorative marketing panel, absolutely positioned so it
           does not push the card off-center. Hidden on narrower viewports
           where it would crowd the centered card. -->
      <sign-up-sidebar class="signup-sidebar-decoration" />
      <!-- centered card -->
      <div class="d-flex flex-column justify-center background">
        <v-card
          class="desktop-card pa-5 hover"
          data-testid="signup-module-card"
          style="width: 550px"
          elevation="2"
        >
          <v-card-text style="height: 93%">
            <sign-up-carousel
              v-model:currentPage="currentPage"
              v-model:companyFormValid="companyFormValid"
              v-model:accountFormValid="accountFormValid"
              v-model:checkoutFormValid="checkoutFormValid"
              v-model:email="email"
              v-model:promoCode="promoCode"
              :pageError="pageError"
              :pageErrorKey="pageErrorKey"
              :accountSubscriptionInfo="accountSubscriptionInfo"
              :title="title"
              :subscriptionID="subscriptionID"
              :signUpSharedUsers="signUpSharedUsers"
              :mobile="false"
              :distributor="distributor"
              :account="account"
              :location="location"
              :countriesAndCodes="countriesAndCodes"
              :promoCodeValid="promoCodeValid"
              :incorrectPromoCode="incorrectPromoCode"
              :totalDue="totalDue"
              @change-company-name="changeCompanyName"
            @resend-email="resendSignUpEmailToDM"
              @change-email="changeEmail"
              @update-promo-code="updatePromoCode"
              @apply-promo-code="applyPromoCode"
            />
          </v-card-text>
          <v-card-actions v-show="currentPage < 3">
            <div class="d-flex align-center">
              <v-btn
                @click="clickBack()"
                data-testid="signup-back"
                v-show="currentPage > 0 && currentPage < 3"
                variant="flat"
                :disabled="loading"
                icon
              ><v-icon icon="chevron_left" /></v-btn>
              <v-spacer />
              <v-progress-circular
                :indeterminate="true"
                color="primary"
                size="32"
                width="4"
                v-if="loading"
                class="mr-3"
              />
              <v-btn
                @click="clickNext()"
                data-testid="signup-next"
                :disabled="nextButtonDisabled"
                variant="flat"
                v-show="currentPage < 3"
                color="primary"
                class="text-white"
                :width="currentPage == 2 ? '100%' : ''"
              >
                <span v-if="currentPage == 2"> Sign Up </span>
                <span v-else> Next </span>
              </v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </div>
    </div>

    <!-- Mobile Version -->
    <div class="d-flex flex-column align-center justify-center background" style="width: 100vw"
 v-else>
      <img
        src="@/assets/logo/logo.png"
        alt=""
        style="max-height: 80px; max-width: 150px"
        class="mb-3 mt-2"
      />
      <v-card
        class="mobile-card pa-2"
        :class="{ 'mobile-card--done': currentPage == 3 }"
        elevation="2"
        data-testid="signup-module-card"
      >
        <v-card-text style="height: 90%">
          <sign-up-carousel
            v-model:currentPage="currentPage"
            v-model:companyFormValid="companyFormValid"
            v-model:accountFormValid="accountFormValid"
            v-model:checkoutFormValid="checkoutFormValid"
            v-model:email="email"
            v-model:promoCode="promoCode"
            :pageError="pageError"
            :pageErrorKey="pageErrorKey"
            :accountSubscriptionInfo="accountSubscriptionInfo"
            :title="title"
            :subscriptionID="subscriptionID"
            :signUpSharedUsers="signUpSharedUsers"
            :mobile="true"
            :distributor="distributor"
            :account="account"
            :location="location"
            :countriesAndCodes="countriesAndCodes"
            :promoCodeValid="promoCodeValid"
            :incorrectPromoCode="incorrectPromoCode"
            :totalDue="totalDue"
            @change-company-name="changeCompanyName"
            @resend-email="resendSignUpEmailToDM"
            @change-email="changeEmail"
            @update-promo-code="updatePromoCode"
            @apply-promo-code="applyPromoCode"
          />
        </v-card-text>
        <v-card-actions v-show="currentPage < 3">
          <div class="d-flex align-center">
            <v-btn
              @click="clickBack()"
              data-testid="signup-back"
              v-show="currentPage > 0 && currentPage < 3"
              variant="flat"
              :disabled="loading"
              icon
            ><v-icon icon="chevron_left" /></v-btn>
            <v-spacer />
            <v-progress-circular
              :indeterminate="true"
              color="primary"
              size="32"
              width="4"
              v-if="loading"
              class="mr-3"
            />
            <v-btn
              @click="clickNext()"
              data-testid="signup-next"
              :disabled="nextButtonDisabled"
              variant="flat"
              v-show="currentPage < 3"
              color="primary"
              class="text-white"
            >
              <span v-if="currentPage == 2"> Sign Up </span>
              <span v-else> Next </span>
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </div>
    <validate-address-dialog
      ref="validateAddressDialog"
      :originalLocation="location"
      :hideMessage="true"
      @useValidatedAddress="updateAddressToValidated"
      @useOriginalAddress="keepOriginalAddress"
      @dialogOpened="loading = false"
      @addressUnparseable="loading = false"
    />
  </div>
</template>

<script>
import store from "@/store";
import rules from "@/mixins/rules";
import helpers from "@/mixins/helpers";
import Location from "@/models/Location";
import ValidateAddressDialog from "@/pages/distributor/store-editor/StoreLocations/ValidateAddressDialog.vue";
import SignUpSidebar from "./SignUpSidebar.vue";
import SignUpCarousel from "./SignUpCarousel.vue";
import { US_JURISDICTIONS } from "@/data/usStates";

export default {
  props: {
    subscriptionID: { type: String, required: true },
    title: String,
    signUpSharedUsers: { type: Boolean, default: false },
  },

  components: {
    "validate-address-dialog": ValidateAddressDialog,
    "sign-up-sidebar": SignUpSidebar,
    "sign-up-carousel": SignUpCarousel,
  },

  mixins: [rules, helpers],

  data() {
    return {
      distributor: {},
      distributorID: null,
      account: {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        phone: ''
      },
      email: null,
      location: Location.generateEmpty(),
      provinceNames: null,
      accountFormValid: false,
      companyFormValid: false,
      checkoutFormValid: false,
      currentPage: null,
      loading: false,
      pageError: null,
      pageErrorKey: 0,
      addressValid: false,
      accountSubscriptionInfo: null,
      mobile: null,
      promoCode: null,
      promoCodeValid: false,
      incorrectPromoCode: false,
      coupon: null,
      couponAmountOff: null,
      couponPercentOff: null,
      countriesAndCodes: [],
    };
  },

  watch: {
    currentPage() {
      this.pageError = null;
      this.pageErrorKey++;
    },
  },

  computed: {
    totalDue() {
      // unit_amount is NULL for a plan with no flat base price (tier-a/b
      // since store_class_001 bill per store, in arrears). Treat it as $0,
      // and never let a fixed-amount promo push the total below zero.
      let cost = Number(
        (this.accountSubscriptionInfo &&
          this.accountSubscriptionInfo.unit_amount) || 0
      );

      if (this.coupon) {
        if (this.coupon.amount_off) {
          cost -= this.coupon.amount_off;
        } else if (this.coupon.percent_off) {
          cost -= (cost * this.coupon.percent_off) / 100;
        }
      }

      return Math.max(0, cost);
    },

    nextButtonDisabled() {
      // The signup takes no payment (the decided trial: no payment method at
      // signup, whatever fees the plan carries), so no card gates the Sign Up button.
      return (
        (!this.companyFormValid && this.currentPage == 0) ||
        (!this.accountFormValid && this.currentPage == 1) ||
        (!this.checkoutFormValid && this.currentPage == 2) ||
        this.loading
      );
    },
  },

  async mounted() {
    this.handleWindowResize();
    this.accountSubscriptionInfo = await this.getSubscriptionInfo(
      this.subscriptionID
    );

    // If subscription info failed to load, stop processing
    if (!this.accountSubscriptionInfo) {
      return;
    }

    this.getCountries();

  },

  created() {
    window.addEventListener("resize", this.handleWindowResize);
  },
  unmounted() {
    window.removeEventListener("resize", this.handleWindowResize);
  },

  methods: {
    async getCountries() {
      let result;
      try {
        result = await this.$axios.get(`/auth/getCountryCodes`);
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      } finally {
        this.countriesAndCodes = result.data;
      }
    },

    updatePromoCode() {
      this.promoCodeValid = false;
      this.incorrectPromoCode = false;
    },

    async applyPromoCode() {
      this.loading = true;
      let response;
      try {
        response = await this.$axios.post(`/stripe/checkPromoCode/`, {
          code: this.promoCode,
        });
      } catch (err) {
        this.$store.dispatch('raiseError', err);
      } finally {
        this.promoCodeValid = response.data.valid;
        this.incorrectPromoCode = !this.promoCodeValid;

        if (this.promoCodeValid) {
          this.coupon = response.data.coupon;
        }

        this.loading = false;
      }
    },

    resendSignUpEmailToDM() {
      const response = this.$axios.post("/auth/resendSignUpEmailToDM", {
        account: this.account,
      });
      if (!response.data || response.data == "success") {
        store.dispatch("raiseSuccess", "Email Sent");
      } else {
        console.log(response.data.error);
      }
    },
    handleWindowResize(e) {
      const width = window.innerWidth;
      if (width < 900) {
        this.mobile = true;
      } else {
        this.mobile = false;
      }
    },

    async signUpUser() {
      let payment_method_id = null;

      // campaign_cost / ow_cost / transaction_cost were dropped in
      // pebble 7 (pricing_engine_006). The backend ignores them if sent.
      this.distributor.max_sales_count = 999;
      this.distributor.email = this.email;
      this.account.email = this.email;
      // No role_id is sent. Self-serve signup always creates the Owner,
      // and the backend resolves that role BY SLUG server-side.
      //
      // This used to send a hardcoded `role_id: 2`, which broke production
      // signup entirely: role ids are sequence-assigned and differ per
      // environment (production's start at 8), so id 2 resolved to nothing
      // and the account INSERT failed on a NOT NULL constraint. Sending a
      // "corrected" id would be the same bug with a different constant.
      // It is also the privilege-escalation fix -- the endpoint is
      // unauthenticated, so a client-supplied role is a client-chosen
      // privilege level and the server now ignores the field.
      //
      // A rejected POST must surface in the wizard: uncaught, it showed
      // nothing and the Sign Up click just appeared to do nothing. 429 is
      // the per-IP signup rate limit (5 per hour); its body is the
      // limiter's own text, so say what happened in plain English instead.
      let response;
      try {
        response = await this.$axios.post("/auth/createTeamTransaction", {
          account: this.account,
          distributor_id: this.distributorID,
          distributor: this.distributor,
          payment_method_id: payment_method_id,
          subscription: this.subscriptionID,
          // The Add Teammates step was removed from the signup wizard; new
          // accounts never provision teammates here. The backend gates all
          // teammate work behind num_sales_reps > 0 / create_teammates, so a
          // zero/empty payload is a no-op there. Teammate-add is directed
          // elsewhere post-signup.
          num_sales_reps: 0,
          create_teammates: false,
          teammates: [],
          coupon:
            this.subscriptionID == "1" && this.promoCodeValid
              ? this.coupon
              : null,
        });
      } catch (err) {
        if (err?.response?.status === 429) {
          this.pageError =
            "Too many sign-up attempts from this network. Please wait an hour and try again.";
        } else if (err?.response?.status === 400 && err.response.data?.error) {
          // A plain-words refusal (phone, [TEST] prefix): show the
          // server's own message.
          this.pageError = err.response.data.error;
        } else {
          this.$store.dispatch("raiseError", err);
          this.pageError =
            "We could not complete your sign-up. Please try again or contact support@threadflow.com.";
        }
        this.pageErrorKey++;
        return false;
      }
      if (!response.data || response.data != "success") {
        if (response.data.error) {
          this.pageError = response.data.error;
        }
        return false;
      } else {
        let discount = 0;
        if (this.coupon) {
          if (this.coupon.amount_off) {
            discount = this.coupon.amount_off;
          } else if (this.coupon.percent_off) {
            discount =
              (this.accountSubscriptionInfo.unit_amount *
                this.coupon.percent_off) /
              100;
          }
        }

        // Track purchase event in Google Analytics if available
        if (typeof gtag !== 'undefined') {
          gtag("event", "purchase", {
            currency: "USD",
            value: this.accountSubscriptionInfo.unit_amount,
            coupon:
              this.subscriptionID == "1" && this.promoCodeValid
                ? this.coupon.name
                : null,
            items: [
              {
                item_name: this.accountSubscriptionInfo.name,
                price: this.accountSubscriptionInfo.unit_amount,
                quantity: 1,
                discount: discount,
              },
            ],
          });
        }

        return true;
      }
    },

    formatPrice(number) {
      return number ? this.numberWithCommas(number.toFixed(2)) : "0.00";
    },

    async getSubscriptionInfo(id) {
      let result;
      try {
        result = await this.$axios.get(`/auth/getSubscriptionInfo/${id}`);
        return result.data;
      } catch (err) {
        this.$store.dispatch('raiseError', err);

        if (err.response && err.response.data && err.response.data.message) {
          if (err.response.data.message.includes('No such price') ||
              err.response.data.message.includes('test mode key')) {
            this.pageError = 'This subscription plan is temporarily unavailable. Please contact support@threadflow.com or try again later.';
          } else {
            this.pageError = 'Unable to load subscription information. Please try again or contact support.';
          }
        } else {
          this.pageError = 'Network error loading subscription details. Please check your connection and try again.';
        }

        this.pageErrorKey++;
        this.$store.dispatch("raiseError", err);
        return null;
      }
    },

    changeCompanyAddress() {
      this.pageError = null;
      this.pageErrorKey++;
    },

    changeCompanyName() {
      this.pageError = null;
      this.pageErrorKey++;
    },

    changeEmail() {
      this.pageError = null;
      this.pageErrorKey++;
    },

    redirectToHome() {
      this.$router.push("/login");
    },

    async payNow() {
      this.loading = true;
      try {
        await this.submit();
      } finally {
        this.loading = false;
      }
    },

    // The tax-nexus canonical shape is FULL state NAMES ("California"),
    // matching src/data/usStates.js and the backend sales-tax consumer
    // (api/src/endpoints/sales_tax.py), which converts the incoming region
    // code to a name before checking nexus_states membership. The signup
    // address flow works in 2-letter CODES (location.region, needed as-is
    // by the ShipEngine address-validation path), so convert the code to
    // its full name before persisting into distributor.nexus_state.
    // Card: Nexus shape mismatch (https://trello.com/c/hSUCQhkp).
    stateNameFromRegion(region) {
      const match = US_JURISDICTIONS.find((j) => j.code === region);
      return match ? match.name : region;
    },

    clickBack() {
      this.currentPage -= 1;
    },

    openValidateAddressDialog() {
      this.loading = true;
      this.$refs.validateAddressDialog.show();
    },

    // The address this wizard collects is the BILLING address. Send it field
    // for field (billing_address_*) alongside the legacy free text (never
    // re-parse that text); the API copies it once into the ship-from columns
    // the Company Profile "Shipping address" form edits. The
    // country select emits a 2-letter code; the untouched default is the
    // word "United States", which the API would refuse as a country code.
    applyStructuredAddress(addr) {
      const text = (value) => (value == null ? "" : String(value).trim());
      const country = text(addr.country);
      this.distributor.billing_address_line1 = text(addr.street_and_number);
      this.distributor.billing_address_line2 = text(addr.suite_unit_etc);
      this.distributor.billing_address_city = text(addr.city);
      this.distributor.billing_address_state = text(addr.region);
      this.distributor.billing_address_postal_code = text(addr.zip_code);
      this.distributor.billing_address_country = /^(us|usa|united states( of america)?)$/i.test(
        country
      )
        ? "US"
        : country;
    },

    updateAddressToValidated(validAddress) {
      this.location.city = validAddress.city;
      this.location.region = validAddress.region;
      this.location.country = validAddress.country;
      this.location.street_and_number = validAddress.street_and_number;
      this.location.zip_code = validAddress.zip_code;
      this.location.suite_unit_etc = validAddress.suite_unit_etc;

      const formattedAddress =
        validAddress.street_and_number +
        ", " +
        (validAddress.suite_unit_etc
          ? validAddress.suite_unit_etc + ", "
          : "") +
        validAddress.city +
        ", " +
        validAddress.region +
        ", " +
        validAddress.zip_code +
        ", " +
        validAddress.country;
      this.distributor.address = formattedAddress;
      this.applyStructuredAddress(validAddress);
      this.distributor.nexus_state = [
        this.stateNameFromRegion(validAddress.region),
      ];
      this.addressValid = true;
      this.loading = false;
      this.currentPage += 1;
    },

    keepOriginalAddress() {
      const formattedAddress =
        this.location.street_and_number +
        ", " +
        (this.location.suite_unit_etc
          ? this.location.suite_unit_etc + ", "
          : "") +
          this.location.city +
        ", " +
        this.location.region +
        ", " +
        this.location.zip_code +
        ", " +
        this.location.country;
      this.distributor.address = formattedAddress;
      this.applyStructuredAddress(this.location);
      this.distributor.nexus_state = [
        this.stateNameFromRegion(this.location.region),
      ];
      this.addressValid = true;
      this.loading = false;
      this.currentPage += 1;
    },

    async clickNext() {
      if (this.currentPage == 0) {
        const distributorNameIsUnique = await this.getDistributorNameIsUnique();
        if (distributorNameIsUnique) {
          if (!this.addressValid) {
            this.openValidateAddressDialog();
          } else {
            this.currentPage += 1;
          }
        } else {
          this.pageError = "Company name already taken";
          this.pageErrorKey++;
        }
      } else if (this.currentPage == 1) {
        const emailIsUnique = await this.getEmailIsUnique(this.email);
        if (emailIsUnique) {
          this.currentPage += 1;
        } else {
          this.pageError = "Email already in use";
          this.pageErrorKey++;
        }
      } else {
        this.payNow();
      }
    },

    async getDistributorNameIsUnique() {
      const response = await this.$axios.post("/auth/getDistributorNameUnique", {
        name: this.distributor.name,
      });
      return response.data;
    },

    async getEmailIsUnique(email) {
      const response = await this.$axios.post("/auth/getEmailUnique", {
        email: email,
      });
      return response.data;
    },

    // DEAD PATH (flagged, not removed -- removal is a separate decision).
    // Nothing in the app calls createAccount(), and `/auth/publicAddAccount`
    // has no route in the backend at all, so this would 404 if it ever ran.
    // The hardcoded `role_id: 2` is dropped regardless: it is the same
    // environment-dependent id that broke production signup, and leaving it
    // here would let the pattern be copied back onto a live path.
    async createAccount() {
      const response = await this.$axios.post("/auth/publicAddAccount", {
        account: this.account,
        distributor_id: this.distributorID,
      });
      if (!response.data || response.data != "success") {
        this.$store.dispatch("raiseError", response.data.error);
        return false;
      } else {
        return true;
      }
    },

    async submit() {
      this.account.email = this.email;

      const accountCreated = await this.signUpUser();

      if (accountCreated) {
        // Advance to the post-submit "Congrats / check your email" state.
        this.currentPage = 3;
      }
    },
  },
};
</script>

<style scoped>
.v-input {
  margin-top: 0px;
  margin-bottom: 0px;
}
.background {
  height: 100vh;
  background-color: #f5f8fa;
}

.mobile-background {
  height: 100vh;
  background-color: #f5f8fa;
  background-color: #f5f8fa;
}

.title {
  font-size: 40px !important;
  font-weight: bold;
}

.credit-card-field {
  border-radius: 25px;
}

.dotted {
  border: none;
  border-top: 2px dotted gray;
  color: #fff;
  background-color: #fff;
}

.tf-logo {
  position: absolute;
  z-index: 1;
  right: 0px;
  top: 0px;
  margin: 16px;
}

.desktop-card {
  padding-right: 25px;
  padding-left: 25px;
  max-width: 600px;
  border-radius: 10px;
  max-height: 850px;
  z-index: 1;
}

/* Desktop layout: center the card in the full viewport. The marketing
   sidebar is a decorative left panel (absolutely positioned) so it does
   not consume flow width or push the card off-center. */
.signup-desktop {
  position: relative;
  width: 100vw;
}

.signup-sidebar-decoration {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  /* Cap the panel to the left third so its right-aligned content stops
     well clear of the centered card. Overrides the component's 50vw. */
  width: 30vw !important;
  z-index: 0;
}

/* Below this width the centered card needs the full viewport; hide the
   decoration so it never crowds or overlaps the card. */
@media (max-width: 1439px) {
  .signup-sidebar-decoration {
    display: none;
  }
}

.mobile-card {
  width: 90%;
  border-radius: 10px;
  max-width: 500px;
  min-height: 600px;
  z-index: 1;
}

/* Post-submit: the card is only as tall as its content. */
.mobile-card--done {
  min-height: 0;
}

.red-stripe {
  position: absolute;
  top: 58%;
  width: 120vw;
  height: 110px;
  transform: rotate(165deg);
  background: #119ae9;
  z-index: 0;
}

.hover {
  z-index: 5px;
  position: relative;
}

p {
  color: #424242;
}
</style>
